import { ref, watch, onUnmounted, getCurrentInstance, nextTick, type Ref } from 'vue'

/**
 * Autosalvataggio "bozza" di un form lato browser (sessionStorage), per non perdere i dati
 * su refresh della pagina, riavvio del server o connessione persa (la tab resta aperta).
 *
 * - Salva una snapshot JSON del form (debounced) in sessionStorage sotto `draft:<key>`.
 * - sessionStorage si auto-cancella alla chiusura della tab → minima esposizione di dati personali.
 * - Al caricamento, `checkForDraft()` rileva una bozza e lascia decidere alla UI se ripristinare
 *   (prompt, NON ripristino trasparente): vedi componente `DraftRestoreBanner.vue`.
 * - Pulizia su salvataggio riuscito (`clearDraft()`), su scarto (`discard()`) e al logout
 *   (vedi `clearAllFormDrafts()` chiamata dallo store auth).
 *
 * Limiti noti:
 * - snapshot via JSON: i campi `Date` vengono serializzati a stringa. Se un form usa oggetti Date
 *   (es. PrimeVue Calendar) passa un `reviveDates` o usa stringhe ISO nel form.
 * - `restore()` fa un merge superficiale (Object.assign) nel form: adatto ai form "piatti" usati nel progetto.
 */

const STORAGE_PREFIX = 'draft:'

interface DraftEnvelope<T> {
  data: T
  ts: number
  /** version del record server al momento del salvataggio (per rilevare conflitti). */
  baseVersion?: number
  /** schema della bozza: se cambia la forma del form, bump → le vecchie bozze si ignorano. */
  schema: number
}

/** Una differenza tra il valore attuale del form (server) e quello salvato nella bozza. */
export interface DraftChange {
  /** Nome del campo (chiave top-level del form). */
  key: string
  /** Valore attualmente nel form (di norma il dato caricato dal server). */
  current: unknown
  /** Valore che la bozza ripristinerebbe. */
  draft: unknown
}

export interface UseFormDraftOptions<T> {
  /** Chiave univoca della bozza (di norma include route + id entità + userId). */
  key: Ref<string> | (() => string)
  /** Ref al form da osservare/ripristinare. */
  form: Ref<T | null>
  /** version corrente caricata dal server (opzionale, per rilevare conflitti). */
  serverVersion?: () => number | undefined
  /** Bump quando cambia la struttura del form (default 1). */
  schema?: number
  /** Debounce salvataggio in ms (default 1000). */
  debounceMs?: number
  /**
   * Chiavi da NON mostrare nell'anteprima delle modifiche (draftChanges). `version` è
   * sempre escluso (campo tecnico di optimistic locking). Aggiungi qui altri campi tecnici
   * o non significativi per l'utente.
   */
  draftExcludeKeys?: string[]
}

/** Campi tecnici sempre esclusi dall'anteprima delle modifiche della bozza. */
const DRAFT_ALWAYS_EXCLUDED = ['version']

/** Rimuove TUTTE le bozze dal sessionStorage (da chiamare al logout). */
export function clearAllFormDrafts(): void {
  try {
    const toRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k)
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k))
  } catch {
    /* storage non disponibile: ignora */
  }
}

export function useFormDraft<T extends object>(opts: UseFormDraftOptions<T>) {
  const schema = opts.schema ?? 1
  const debounceMs = opts.debounceMs ?? 1000
  const keyFn = typeof opts.key === 'function' ? opts.key : () => (opts.key as Ref<string>).value
  const storageKey = () => STORAGE_PREFIX + keyFn()

  const draftAvailable = ref(false)
  const draftTimestamp = ref<number | null>(null)
  /** true se la version server è avanzata rispetto a quella della bozza (modifica concorrente). */
  const draftConflict = ref(false)
  /** true se il form è stato modificato dopo il load e non è ancora stato salvato. */
  const isDirty = ref(false)
  /** Elenco dei campi che la bozza cambierebbe rispetto ai dati attuali (per l'anteprima pre-ripristino). */
  const draftChanges = ref<DraftChange[]>([])

  let pending: DraftEnvelope<T> | null = null
  let armed = false // si arma solo dopo checkForDraft + nextTick (= dopo il load dei dati server)
  let baseline: string | null = null // snapshot del form al load: isDirty = (form ≠ baseline)
  let timer: ReturnType<typeof setTimeout> | null = null
  // Dopo clearDraft() le modifiche fatte dal codice nello stesso giro (svuotare il form del dialog, ricaricare il
  // salvato) sono il nuovo stato pulito, non modifiche dell'utente: senza questo, chiudere il dialog dopo «Salva»
  // chiedeva «Ci sono modifiche non salvate» (segnalazione di Giuseppe Maltese, 22/10/2026).
  let riallinea = false
  let riallineaTimer: ReturnType<typeof setTimeout> | null = null

  function snapshot(): string | null {
    try {
      return JSON.stringify(opts.form.value)
    } catch {
      return null
    }
  }

  /**
   * Calcola le differenze tra la bozza (`pending.data`) e i valori attuali del form,
   * per mostrare all'utente COSA verrebbe ripristinato. Confronto top-level via JSON.
   */
  function computeDraftChanges(): DraftChange[] {
    if (!pending || opts.form.value == null) return []
    const excluded = new Set([...DRAFT_ALWAYS_EXCLUDED, ...(opts.draftExcludeKeys ?? [])])
    const cur = opts.form.value as Record<string, unknown>
    const draft = pending.data as Record<string, unknown>
    const keys = new Set([...Object.keys(cur), ...Object.keys(draft)])
    const changes: DraftChange[] = []
    for (const key of keys) {
      if (excluded.has(key)) continue
      if (JSON.stringify(cur[key]) !== JSON.stringify(draft[key])) {
        changes.push({ key, current: cur[key], draft: draft[key] })
      }
    }
    return changes
  }

  function persist(): void {
    if (opts.form.value == null) return
    try {
      const env: DraftEnvelope<T> = {
        data: JSON.parse(JSON.stringify(opts.form.value)),
        ts: Date.now(),
        baseVersion: opts.serverVersion?.(),
        schema,
      }
      sessionStorage.setItem(storageKey(), JSON.stringify(env))
    } catch {
      /* quota/serializzazione: ignora (la bozza è best-effort) */
    }
  }

  watch(
    opts.form,
    () => {
      if (!armed) return
      // dirty SOLO se il form differisce davvero dal baseline (niente falsi positivi da
      // normalizzazioni dei componenti o da modifiche poi annullate dall'utente)
      const cur = snapshot()
      if (riallinea) baseline = cur
      isDirty.value = cur !== baseline
      if (timer) clearTimeout(timer)
      if (isDirty.value) {
        timer = setTimeout(persist, debounceMs)
      } else {
        // tornati uguali al baseline: nessuna bozza stale da tenere
        try {
          sessionStorage.removeItem(storageKey())
        } catch {
          /* ignora */
        }
      }
    },
    { deep: true },
  )

  /** Da chiamare DOPO aver caricato i dati del server nel form. */
  function checkForDraft(): void {
    try {
      const raw = sessionStorage.getItem(storageKey())
      if (raw) {
        const env = JSON.parse(raw) as DraftEnvelope<T>
        if (env.schema !== schema) {
          clearDraft()
        } else {
          pending = env
          draftTimestamp.value = env.ts
          const sv = opts.serverVersion?.()
          draftConflict.value = env.baseVersion != null && sv != null && sv !== env.baseVersion
          draftChanges.value = computeDraftChanges()
          // Se la bozza non differisce in nulla dai dati attuali, non ha senso proporre il ripristino.
          draftAvailable.value = draftChanges.value.length > 0
          if (!draftAvailable.value) clearDraft()
        }
      }
    } catch {
      /* bozza corrotta: ignora */
    }
    // Arma l'autosave SOLO dopo il flush delle mutazioni del load: così il caricamento dati
    // (e le normalizzazioni dei componenti al mount) NON marcano il form come "sporco".
    void nextTick(() => {
      baseline = snapshot()
      armed = true
    })
  }

  /** Ripristina la bozza nel form (merge superficiale). Dopo il ripristino il form è "sporco". */
  function restore(): void {
    if (pending && opts.form.value != null) {
      Object.assign(opts.form.value as object, pending.data as object)
      isDirty.value = snapshot() !== baseline
    }
    dismiss()
  }

  /** Scarta la bozza (rimuove dallo storage e chiude il prompt). */
  function discard(): void {
    clearDraft()
    dismiss()
  }

  function dismiss(): void {
    draftAvailable.value = false
    pending = null
    draftChanges.value = []
  }

  /**
   * Rimuove la bozza dallo storage (da chiamare dopo un salvataggio riuscito).
   * Lo stato corrente diventa il nuovo baseline → isDirty torna false.
   */
  function clearDraft(): void {
    try {
      sessionStorage.removeItem(storageKey())
    } catch {
      /* ignora */
    }
    baseline = snapshot()
    isDirty.value = false
    // finestra chiusa al primo macrotask: copre il codice sincrono e il flush dei watcher di Vue (microtask)
    riallinea = true
    if (riallineaTimer) clearTimeout(riallineaTimer)
    riallineaTimer = setTimeout(() => {
      riallinea = false
      riallineaTimer = null
    }, 0)
  }

  // onUnmounted solo se dentro un componente (il composable è usabile anche fuori, es. nei test)
  if (getCurrentInstance()) {
    onUnmounted(() => {
      if (timer) clearTimeout(timer)
      if (riallineaTimer) clearTimeout(riallineaTimer)
    })
  }

  return { draftAvailable, draftTimestamp, draftConflict, draftChanges, isDirty, checkForDraft, restore, discard, clearDraft }
}
